/**
 * <Icon> — vector glyph atom (Lucide).
 *
 * Brand core V13, chap. 5 : « jamais de remplissage, grille 24 px, tailles 16 · 20 · 24, marine sur
 * clair / blanc sur nuit. Jamais de version pleine, jamais d'emoji. » Le trait, 1,5 px dans le livre,
 * est passé à 1,75 le 20/10 (`STROKE_WIDTH`). It is pinned here so no call-site can drift.
 *
 * `name` stays a FontAwesome 5 name: `iconRegistry.ts` maps it to the Lucide component actually
 * drawn, so the migration costs nothing at the existing call-sites. The union is the registry's
 * keys, so an unmapped name is a compile error, not a blank box.
 *
 * `solid` fills the glyph. It contradicts « jamais de version pleine » for an *interface* icon and
 * is reserved for a **state mark** — the earned star, the reached flag, the ticked box — where the
 * fill carries the information. Don't reach for it to make a toolbar icon heavier.
 *
 * No hex literals: `color` is a `SemanticColorKey`. Lucide takes its color as a plain prop (not a
 * style), so the token is resolved to its runtime value through the Unistyles `useUnistyles()`
 * hook rather than a direct token-module import.
 */
import React from "react"
import { useUnistyles } from "react-native-unistyles"

import { type SemanticColorKey } from "../../../theme/tokens/colors"
import { ICONS, type IconName } from "./iconRegistry"

export type { IconName }

/**
 * Épaisseur de trait, dans la grille de 24 de Lucide : 1,75, soit 1,2 px réel à 16, 1,5 px à 20 et
 * 1,75 px à 24. Le livre disait 1,5, trop fin aux petites tailles (1 px réel à 16) ; 2, le trait
 * dessiné de Lucide, alourdit le 24 (Wajih, 20/10).
 */
const STROKE_WIDTH = 1.75

export type IconProps = {
    name: IconName
    /** Pixel size — defaults to 20 (the book's middle step: 16 · 20 · 24). */
    size?: number
    /** Semantic color token — defaults to `text-primary`. */
    color?: SemanticColorKey
    /** Fill the glyph — state marks only (étoile gagnée, drapeau atteint, case cochée). */
    solid?: boolean
    testID?: string
}

export function Icon(props: IconProps) {
    const { name, size = 20, color = "text-primary", solid = false, testID } = props
    const { theme } = useUnistyles()

    const Glyph = ICONS[name]
    const stroke = theme.colors[color]

    return (
        <Glyph size={size} color={stroke} strokeWidth={STROKE_WIDTH} fill={solid ? stroke : "none"} testID={testID} />
    )
}
